<template>
  <!-- Desktop Drawer Variant -->
  <transition name="related-slide-right">
    <div
      v-if="variant === 'drawer-desktop' && products?.length"
      class="hidden h-full w-[200px] shrink-0 flex-col bg-white lg:flex"
    >
      <div
        class="ui-text-m bg-boticelli-blue-300 px-4 py-6 text-center uppercase"
      >
        {{ props.title }}
      </div>
      <div class="h-[calc(100%-72px)] overflow-hidden px-4 pb-4 pt-4">
        <Swiper
          v-if="shouldRender"
          class="max-h-full !overflow-visible"
          direction="vertical"
          slides-per-view="auto"
          :modules="modules"
          :mousewheel="{
            forceToAxis: true,
          }"
          :free-mode="true"
          :space-between="16"
        >
          <SwiperSlide v-for="(item, index) in products" :key="item.id">
            <ProductListCarouselCard
              :product="item"
              :product-index="index"
              :plp-type="plpTypes.default"
              :statistic-item-list-id="statisticItemListId"
              is-card-vertical
              is-card-drawer
              :statistic-item-list-name="statisticItemListName"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </transition>

  <!-- Mobile Drawer Variant -->
  <transition name="related-slide-down">
    <div
      v-if="variant === 'drawer-mobile' && products?.length"
      class="bg-boticelli-blue-300 mt-auto pb-3 pt-6 lg:hidden"
    >
      <h3 class="ui-text-m mb-2 px-4 uppercase">
        {{ props.title }}
      </h3>
      <UiScrollableSlider
        v-if="shouldRender"
        :slides-count="1.5"
        :items-list="products"
        :gap-x="8"
      >
        <template #card="{ item, idx }">
          <ProductListCarouselCard
            :product="item"
            :product-index="idx"
            :plp-type="plpTypes.default"
            :statistic-item-list-id="statisticItemListId"
            is-card-drawer
            :statistic-item-list-name="statisticItemListName"
          />
        </template>
      </UiScrollableSlider>
    </div>
  </transition>

  <!-- Page Variant -->

  <RelatedProductListCarousel
    v-if="variant === 'page' && products?.length"
    :products="products"
    :title="props.title"
    class="mt-4 pb-3 max-lg:w-full lg:px-4 lg:pb-9"
  />
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import { Mousewheel, FreeMode } from "swiper";
import { CatalogData } from "ecom-types";
import ProductListCarouselCard from "../product/ProductListCarouselCard.vue";
import { plpTypes } from "~/configs";
import RelatedProductListCarousel from "../product/RelatedProductListCarousel.vue";
import UiScrollableSlider from "../Ui/UiScrollableSlider.vue";

interface Props {
  variant: "drawer-desktop" | "drawer-mobile" | "page";
  shouldRender?: boolean;
  products: CatalogData[];
  title?: string;
}

const props = withDefaults(defineProps<Props>(), {
  shouldRender: true,
  products: () => [],
  title: "",
});

const statisticItemListName = computed(() =>
  getItemListName("Shopping Cart", props.title),
);

const statisticItemListId = computed(() => "cart");

function getItemListName(prefix: string, suffix: string): string {
  return suffix ? `${prefix} - ${suffix}` : prefix;
}

const modules = [Mousewheel, FreeMode];
</script>

<style scoped>
.related-slide-right-enter-active,
.related-slide-right-leave-active {
  transition: 0.5s;
}

.related-slide-right-enter-from,
.related-slide-right-leave-to {
  transform: translateX(100%);
}

.related-slide-down-enter-active,
.related-slide-down-leave-active {
  transition: 0.5s;
}

.related-slide-down-enter-from,
.related-slide-down-leave-to {
  transform: translateY(100%);
}
</style>
